import {
  pgTable,
  uuid,
  text,
  boolean,
  integer,
  jsonb,
  timestamp,
  index,
  check,
} from "drizzle-orm/pg-core";
import { sql } from "drizzle-orm";

const tz = { withTimezone: true } as const;

// ---------------------------------------------------------------------------
// Conversation Flow Config (DM-specific, execution via workflow engine)
// ---------------------------------------------------------------------------
// Per council ruling: conversation_flows stores DM-specific config ONLY.
// The actual execution goes through the existing workflow engine
// (workflow_definitions, workflow_enrollments, workflow_step_logs).

export const FLOW_STATUSES = ["draft", "active", "paused", "archived"] as const;
export type FlowStatus = (typeof FLOW_STATUSES)[number];

export const TRIGGER_PLATFORMS = ["instagram", "facebook", "tiktok", "whatsapp", "messenger"] as const;
export type TriggerPlatform = (typeof TRIGGER_PLATFORMS)[number];

/** DM flow initial message config */
export interface DmFlowConfig {
  initialMessage: string;
  buttons?: Array<{ label: string; value: string }>;
  aiHybridEnabled?: boolean;
  knowledgeBaseId?: string;
  optOutText?: string; // GDPR: mandatory opt-out in first DM
}

/** Per-platform rate limit tracking */
export interface PlatformRateLimits {
  instagram?: { sentThisHour: number; maxPerHour: number; lastResetAt: string };
  facebook?: { sentThisHour: number; maxPerHour: number; lastResetAt: string };
  tiktok?: { sentThisHour: number; maxPerHour: number; lastResetAt: string };
}

export const conversationFlows = pgTable("conversation_flows", {
  id: uuid("id").primaryKey().defaultRandom(),
  tenantId: uuid("tenant_id").notNull(),
  name: text("name").notNull(),
  triggerKeyword: text("trigger_keyword").notNull(),
  triggerPlatforms: text("trigger_platforms").array().notNull(), // e.g., ["instagram", "facebook"]
  autoLikeComment: boolean("auto_like_comment").notNull().default(true),
  // DM config (initial message, buttons, AI mode)
  flowConfig: jsonb("flow_config").$type<DmFlowConfig>().notNull(),
  // Links to workflow engine for execution
  workflowDefinitionId: uuid("workflow_definition_id"),
  // Rate limit state (per council condition #2)
  rateLimits: jsonb("rate_limits").$type<PlatformRateLimits>().default({}),
  // Status + metrics
  status: text("status").notNull().default("draft"),
  totalConversations: integer("total_conversations").notNull().default(0),
  totalQualified: integer("total_qualified").notNull().default(0),
  totalBooked: integer("total_booked").notNull().default(0),
  totalRevenueCents: integer("total_revenue_cents").notNull().default(0),
  createdAt: timestamp("created_at", tz).notNull().defaultNow(),
  updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
}, (table) => [
  index("idx_conversation_flows_tenant").on(table.tenantId),
  index("idx_conversation_flows_keyword").on(table.tenantId, table.triggerKeyword),
  index("idx_conversation_flows_status").on(table.status),
  check("chk_conversation_flow_status", sql`${table.status} IN ('draft', 'active', 'paused', 'archived')`),
]);

// ---------------------------------------------------------------------------
// Office Displays — 4 types, built with Creative Engine
// ---------------------------------------------------------------------------
// Per council condition #3: display URLs require device auth token.
// Per council condition #5: stale data >5min shows warning banner.

export const DISPLAY_TYPES = [
  "waiting_room",
  "front_desk",
  "team_board",
  "kiosk",
  "walk_in",
] as const;
export type DisplayType = (typeof DISPLAY_TYPES)[number];

export interface DisplayConfig {
  slides?: Array<{
    type: string;
    content: Record<string, unknown>;
    durationSeconds: number;
  }>;
  refreshIntervalSeconds?: number;
  staleWarningThresholdSeconds?: number; // Default 300 (5 min) per council condition #5
  theme?: { primaryColor?: string; logoUrl?: string };
}

export const officeDisplays = pgTable("office_displays", {
  id: uuid("id").primaryKey().defaultRandom(),
  tenantId: uuid("tenant_id").notNull(),
  name: text("name").notNull(),
  displayType: text("display_type").notNull(),
  config: jsonb("config").$type<DisplayConfig>().notNull().default({}),
  // Device auth (council condition #3)
  deviceToken: text("device_token"), // Hashed session token for display device
  // URL path: /display/{tenantSlug}/{displayId}
  isActive: boolean("is_active").notNull().default(true),
  lastDataRefreshAt: timestamp("last_data_refresh_at", tz),
  createdAt: timestamp("created_at", tz).notNull().defaultNow(),
  updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
}, (table) => [
  index("idx_office_displays_tenant").on(table.tenantId),
  index("idx_office_displays_type").on(table.tenantId, table.displayType),
  check("chk_display_type", sql`${table.displayType} IN ('waiting_room', 'front_desk', 'team_board', 'kiosk', 'walk_in')`),
]);
